import { AxiosResponse } from "axios";
import AuthTokenHolder from "./AuthTokenHolder";
import {
    getIsShowErrorModal,
    showErrorModal,
    showEwaybillAccountReAuthModal,
    showRenewUpgradeModal,
    showShopExpiredModal,
    showShopOrderVolumeLimitExceededModal,
    showShopPremiumModal,
    showShopReAuthModal,
    showUnrecognizableTableHeaderErrorModal,
} from "./utils";
import { stillSuccessUrlList, authInvalidNotShowModalList, authInvalidRejectList } from "@/config";
import { LoggerInfoTag, postLogInfo } from "../api/log";
import { parse as urlParse } from "url";
import { ExtendAxiosResponse } from "./types";
import ShopDTO from "@/dto/shop/ShopDTO";

/**
 * 数据响应成功处理方法
 */
function successResponseHandler(response: ExtendAxiosResponse) {
    return response.data?.data;
}

/**
 * 授权过期响应处理函数
 */
function authInvalidResponseHandler() {
    AuthTokenHolder.clearToken();

    if (import.meta.env.VITE_IS_QN) {
        return Promise.reject(new Error("授权已过期，请关闭发货易重新打开试试"));
    }
    return Promise.reject(new Error("登录已过期，请刷新页面后重新登录"));
}

/**
 * 部分店铺订单权限出现问题的处理方法
 */
async function shopAuthInvalidResponseHandler(response: ExtendAxiosResponse) {
    const data = response?.data?.data;
    const requestUrl = response?.config?.url || "";

    if (response?.config?.hideModal) {
        return Promise.reject(new Error("店铺授权过期，请重新授权"));
    }

    const urlParsed = urlParse(requestUrl);

    if (authInvalidNotShowModalList.some((url) => urlParsed.path === url)) {
        return Promise.resolve();
    }

    showShopReAuthModal(data);

    if (authInvalidRejectList.some((url) => requestUrl.includes(url))) {
        return Promise.reject(new Error("店铺授权过期，请重新授权"));
    }

    return Promise.reject(new Error("店铺授权过期，请重新授权"));
}

function ewaybillAccountInvalidResponseHandler(response: ExtendAxiosResponse) {
    const data = response?.data?.data;
    const requestUrl = response?.config?.url || "";

    showEwaybillAccountReAuthModal(data);

    if (authInvalidRejectList.some((url) => requestUrl.includes(url))) {
        return Promise.reject({
            message: "电子面单账号授权过期，请重新授权",
            data: {},
        });
    }

    return Promise.reject({ response: { status: 502 } });
}

function shopExpiredResponseHandler(response: ExtendAxiosResponse) {
    const data = response?.data?.data;

    if (!response?.config?.hideModal) {
        switch (response.config.url) {
            case "/app_subscription/order_upgrade/get_upgrade_differential":
                showRenewUpgradeModal(data);
                break;

            default:
                showShopExpiredModal(data);
                break;
        }
    }

    const message = response?.data?.message || "该店铺已过期，请续费";

    return Promise.reject(new Error(message));
}

/**
 * 高级版拦截校验
 */
function shopPremiumResponseHandler(response: ExtendAxiosResponse) {
    const requestUrl = response?.config?.url || "";
    const errorMessage = response?.data?.message;

    const pathPremiumInfo = response?.config?.pathPremiumValidator?.();

    if (pathPremiumInfo) {
        showShopPremiumModal(errorMessage, pathPremiumInfo.shopId, pathPremiumInfo.type);
    } else {
        const isShowErrorModal = getIsShowErrorModal(requestUrl);

        if (isShowErrorModal && !response?.config?.hideModal) {
            showErrorModal("错误信息", errorMessage);
        }
    }

    return Promise.reject(new Error(errorMessage));
}

async function shopOrderVolumeLimitExceededHandler(response: ExtendAxiosResponse) {
    const requestUrl = response?.config?.url || "";
    const responseData = response?.data?.data as Pick<ShopDTO, "id">;
    let shopId = responseData?.id;
    const errorMessage = response?.data?.message;

    // 千牛小程序可能拿不到shopId，尝试从请求中携带的数据拿shopId
    if (!shopId) {
        shopId = response?.config?.data?.shopId || response?.config?.params?.shopId;
    }

    const isShowErrorModal = getIsShowErrorModal(requestUrl);

    if (isShowErrorModal) {
        try {
            await showShopOrderVolumeLimitExceededModal(errorMessage, shopId);
        } catch (error) {
            console.error(error);
        }
    }

    return Promise.reject(new Error(errorMessage));
}

async function unrecognizableTableHeaderResponseHandler(response: ExtendAxiosResponse) {
    showUnrecognizableTableHeaderErrorModal();

    return Promise.reject(new Error(response?.data?.message || "无法识别表格表头"));
}

/**
 * 最后默认兜底的错误处理方式
 */
function defaultResponseHandler(response: ExtendAxiosResponse) {
    const requestUrl = response?.config?.url || "";

    const errorMessage = response?.data?.message;

    const urlParsed = urlParse(requestUrl);

    const isStillSuccess = stillSuccessUrlList.some((url) => urlParsed.path === url);

    if (isStillSuccess) {
        return Promise.resolve();
    }

    // 回传日志
    if (errorMessage?.includes("shopId为空，请联系客服")) {
        let paramsString = "";
        let bodyString = "";

        try {
            paramsString = JSON.stringify(response?.config?.params);
            bodyString = JSON.stringify(response?.config?.data);
        } catch (error) {}

        postLogInfo({
            tag: LoggerInfoTag.REQUEST_MISSING_PARAMS,
            content: `request method: ${response?.config?.method}, baseUrl: ${response?.config?.baseURL}, url: ${response?.config?.url} params: ${paramsString}, body: ${bodyString}`,
        });
    }

    // 网络请求错误
    if (errorMessage?.includes("Network Error") && !requestUrl?.includes("/frontend_logger/info")) {
        let paramsString = "";
        let bodyString = "";

        try {
            paramsString = JSON.stringify(response?.config?.params);
            bodyString = JSON.stringify(response?.config?.data);
        } catch (error) {}

        // 请求错误发送日志回系统
        postLogInfo({
            tag: LoggerInfoTag.REQUEST_NETWORK_ERROR,
            content: `request network error: ${response?.config?.method}, path:${requestUrl}, server:${response?.config?.baseURL}, params data: ${paramsString}, body data: ${bodyString}`,
        });
    }

    // 千牛端用户续费过期，直接提示续费
    if (errorMessage === "非法或过期的SessionKey参数，请使用有效的SessionKey参数") {
        showShopExpiredModal({});

        return Promise.reject(new Error(errorMessage));
    }

    const isShowErrorModal = getIsShowErrorModal(requestUrl);

    if (isShowErrorModal && !response?.config?.hideModal) {
        showErrorModal("错误信息", errorMessage);
    }

    return Promise.reject(new Error(errorMessage));
}

/**
 *
 * 已经拿到响应数据，根据顶层发货易数据结构顶层code 和 successful 属性判断业务失败与否
 *
 * 传入 successful || code 即可
 */
export default function responseHandler(response: AxiosResponse): any {
    /**
     * 发货易封装数据结构顶层的code
     */
    const seCode = response.data?.code as number;
    const successful = response.data?.successful as boolean;

    switch (successful || seCode) {
        // 发货易的成功状态
        case 0:
        case true:
            return successResponseHandler(response);

        // 权限校验失败状态
        case 401:
        case 403:
            return authInvalidResponseHandler();

        // 店铺授权失败状态
        case 501:
            return shopAuthInvalidResponseHandler(response);

        // 电子面单账号授权过期
        case 502:
            return ewaybillAccountInvalidResponseHandler(response);

        /** 发货易付费渠道店铺过期 */
        case 503:
            return shopExpiredResponseHandler(response);

        /** 发货易高级版限制报错 */
        case 10001:
            return shopPremiumResponseHandler(response);

        /** 订单超量 */
        case 10002:
            return shopOrderVolumeLimitExceededHandler(response);

        /** 表格表头无法适配 */
        case 400404:
            return unrecognizableTableHeaderResponseHandler(response);

        // 权限校验失败状态
        default:
            return defaultResponseHandler(response);
    }
}
